<template>
  <section
    :id="anchorId"
    class="relative overflow-hidden"
    :style="{ backgroundColor: 'var(--sideout-navy)' }"
  >
    <!-- Background image + gradient overlay -->
    <div
      v-if="heroBackground"
      class="pointer-events-none absolute inset-0 bg-cover bg-center opacity-50"
      :style="{ backgroundImage: `url(${heroBackground})` }"
    />
    <div class="v2-hero__overlay pointer-events-none absolute inset-0" />

    <!-- Mobile trims the top run below the sticky header (Rick 2026-06-06:
         "reduce the space below the menu") — pt-10 instead of py-20 — while md+
         keeps the original generous py-20 / lg:py-24 hero breathing room. -->
    <div class="relative mx-auto grid max-w-[1400px] items-center gap-10 px-6 pt-10 pb-16 md:grid-cols-5 md:gap-12 md:px-12 md:py-20 lg:px-16 lg:py-24">
      <!-- Left column: headline + body -->
      <div class="md:col-span-3">
        <p
          v-if="heroBadge"
          class="sideout-eyebrow v2-hero__eyebrow"
          :style="{ color: 'var(--sideout-pink-light)', letterSpacing: '0.2em' }"
        >
          {{ heroBadge }}
        </p>
        <a
          v-if="parentCampaignLink"
          :href="parentCampaignLink.href"
          class="v2-hero__parent-link"
        >
          Supporting {{ parentCampaignLink.name }}
        </a>
        <h1
          class="mt-4 text-white"
          :style="{
            fontWeight: 800,
            fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
            lineHeight: '1.0',
            letterSpacing: '-0.01em',
          }"
        >
          {{ heroHeadline }}
        </h1>
        <div
          v-if="heroBody"
          class="v2-rich-text mt-6 max-w-2xl text-lg leading-relaxed"
          :style="{ color: 'rgba(255,255,255,0.9)' }"
          v-html="heroBody"
        ></div>
        <!-- Event calendar: compact date-box grid sitting under the hero
             copy. Self-hides when the campaign has zero upcoming events. -->
        <V2HeroEvents v-if="campaign && campaign.id" :campaign-id="campaign.id" />
      </div>

      <!-- Right column: progress card -->
      <div class="md:col-span-2 flex justify-center md:justify-end">
        <div class="progress-card">
          <!-- Eyebrow -->
          <span class="progress-card__eyebrow">Campaign Progress</span>

          <!-- Dollar amount with trending-up icon + subtitle -->
          <div class="progress-card__amount-block">
            <div class="progress-card__amount-row">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="progress-card__amount-icon" aria-hidden="true">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                <polyline points="16 7 22 7 22 13"/>
              </svg>
              <span class="progress-card__amount">{{ progress.formattedRaised.value }}</span>
            </div>
            <span class="progress-card__subtitle">
              raised of {{ progress.formattedGoal.value }} goal
            </span>
          </div>

          <!-- Progress bar group -->
          <div class="progress-card__bar-group">
            <div class="progress-card__bar-header">
              <span class="progress-card__pct">{{ progress.percent.value }}%</span>
              <span class="progress-card__to-go">{{ progress.formattedGap.value }} to go</span>
            </div>
            <div class="progress-card__track">
              <div
                class="progress-card__fill"
                :style="{ width: progress.percent.value + '%' }"
              />
            </div>
            <!-- Milestones: absolute-positioned dot-over-label markers -->
            <div class="progress-card__milestones">
              <div
                v-for="(m, i) in progress.milestones.value"
                :key="m.value"
                class="progress-card__milestone"
                :class="{ 'progress-card__milestone--reached': m.reached }"
                :style="{ left: (((i + 1) / progress.milestones.value.length) * 100) + '%' }"
              >
                <span class="progress-card__milestone-dot" aria-hidden="true"></span>
                <span class="progress-card__milestone-label">{{ m.label }}</span>
              </div>
            </div>
          </div>

          <!-- Counters (inline: icon + value + label) -->
          <div class="progress-card__counters">
            <component
              :is="impactScoreLink ? 'a' : 'div'"
              :href="impactScoreLink || null"
              :title="impactScoreLink ? 'View impact leaderboard' : null"
              class="progress-card__counter"
              :class="{ 'progress-card__counter--link': impactScoreLink }"
            >
              <!-- Lucide award icon (amber) -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="progress-card__counter-icon progress-card__counter-icon--amber" aria-hidden="true">
                <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"/>
                <circle cx="12" cy="8" r="6"/>
              </svg>
              <span class="progress-card__counter-value">{{ impactCount }}</span>
              <span class="progress-card__counter-label">Impact</span>
            </component>
            <div class="progress-card__counter">
              <!-- Lucide megaphone icon (brand pink) -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="progress-card__counter-icon progress-card__counter-icon--pink" aria-hidden="true">
                <path d="m3 11 18-5v12L3 14v-3z"/>
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
              </svg>
              <span class="progress-card__counter-value">{{ supportingCount }}</span>
              <span class="progress-card__counter-label">Campaigns</span>
            </div>
          </div>

          <!-- Actions: full-width Donate stacked over a text link that
               anchors to the lower-page Support section. Per Rick's
               feedback round (2026-05-23): the secondary Share button was
               removed — sharing now lives inline in V2SupportOnboarding,
               so we route visitors there rather than opening a modal. -->
          <div class="progress-card__actions">
            <button
              type="button"
              class="progress-card__btn progress-card__btn--donate"
              @click="openDonate"
            >
              <!-- Lucide heart -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="progress-card__btn-icon" aria-hidden="true">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
              </svg>
              Donate Now
            </button>
            <a
              class="progress-card__support-link"
              :href="supportPageUrl || '#support-campaign'"
              @click="scrollToSupport"
            >
              Other ways to support our campaign
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { isUnmodifiedPrimaryClick } from '../../../../composables/useCampaignShare'
import { useCampaignProgress } from '../../../../composables/useCampaignProgress'
import { scrollToInPageAnchor } from '../../../../composables/useInPageScroll'
import V2HeroEvents from './V2HeroEvents.vue'

export default {
  name: 'V2Hero',
  components: { V2HeroEvents },
  props: {
    supportPageUrl: { type: String, default: null },
    campaign: { type: Object, required: true },
    hero: { type: Object, default: () => ({}) },
    anchorId: { type: String, default: 'hero' },
  },
  computed: {
    // A subcampaign is any campaign below the foundation. Mirrors
    // Campaign::getIsFoundationAttribute (isFoundation === empty(parent_id));
    // isFoundation is appended to the campaign JSON, with a parent_id
    // fallback so detection holds even if the flag is ever absent.
    isSubcampaign() {
      const c = this.campaign || {}
      if (typeof c.isFoundation === 'boolean') return !c.isFoundation
      return Boolean(c.parent_id)
    },
    parentCampaignLink() {
      const parent = this.campaign?.parent
      const name = typeof parent?.name === 'string' ? parent.name.trim() : ''
      if (!this.isSubcampaign || !parent?.id || !name) return null
      if (String(parent.id) === String(this.campaign.id)) return null

      return { href: `/campaigns/${parent.id}`, name }
    },
    heroBadge() {
      // On subcampaigns, surface the campaign's own name as the hero eyebrow
      // so visitors immediately see which team/campaign they're supporting,
      // instead of the generic foundation badge (e.g. "DIG PINK® CAMPAIGN")
      // inherited from the shared template. The foundation keeps its badge.
      if (this.isSubcampaign) return this.campaign?.name || this.hero?.badge
      return this.hero?.badge
    },
    heroHeadline() { return this.hero?.headline || this.campaign.name },
    heroBody() { return this.hero?.body || this.campaign.description || '' },
    heroBackground() { return this.hero?.background_image || this.campaign.hero_image_path || null },
    impactCount() {
      return this.campaign?.impact_score ? Number(this.campaign.impact_score).toFixed(1) : 0
    },
    impactScoreLink() {
      if (!this.campaign?.impact_score || !this.campaign?.foundation_id) return null
      const targetId = this.campaign.parent_campaign_id || this.campaign.id
      return `/campaigns/${targetId}/impact#leaderboard`
    },
    supportingCount() {
      return this.campaign?.direct_child_count || this.campaign?.children_node_count || 0
    },
  },
  setup(props) {
    const progress = useCampaignProgress(() => props.campaign)
    return { progress }
  },
  methods: {
    openDonate() {
      this.emitter.emit('open-donation-modal')
    },
    scrollToSupport(e) {
      if (this.supportPageUrl) {
        if (!isUnmodifiedPrimaryClick(e)) return
        // Support Hub rollout: open the takeover in place. The href stays
        // on the anchor so the shareable link is still copyable.
        e?.preventDefault?.()
        this.emitter.emit('open-support-hub', { returnFocus: e?.currentTarget })
        return
      }
      // Smooth-scroll to the lower-page "Other ways to support" section
      // (V2SupportOnboarding). The shared helper owns the sticky-header
      // offset so this anchor lands at the same y-position as every other
      // in-page anchor; when the target is missing it's a no-op and the
      // native href="#support-campaign" jump takes over.
      scrollToInPageAnchor('support-campaign', e)
    },
  },
}
</script>

<style scoped>
.v2-hero__parent-link {
  display: inline-block;
  max-width: 100%;
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
  text-decoration: underline;
  text-underline-offset: 4px;
}
.v2-hero__parent-link:hover {
  color: #ffffff;
}
.v2-hero__parent-link:focus-visible {
  outline: 2px solid var(--sideout-pink-light);
  outline-offset: 4px;
  border-radius: 2px;
}

/* Hero overlay gradient — atmospheric navy→pink wash over the campaign
   photo. `background-size: 200% 200%` gives the keyframe animation room
   to shift the gradient origin across the viewport. 20s duration with
   ease-in-out produces a slow, unhurried drift — barely perceptible
   per-second but visibly alive over the scale of reading the hero copy. */
.v2-hero__overlay {
  background: linear-gradient(135deg, rgba(29, 37, 73, 0.92) 0%, rgba(225, 14, 120, 0.42) 100%);
  background-size: 200% 200%;
  animation: v2-hero-gradient-drift 20s ease-in-out infinite;
}
@keyframes v2-hero-gradient-drift {
  0%, 100% { background-position: 0% 0%; }
  50%      { background-position: 100% 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .v2-hero__overlay { animation: none; }
}

/* Progress card — re-sampled from Lovable's exact DOM. Every token
   below was captured via Playwright browser_evaluate on the live
   preview. Structure is `text-center` with a max-w-md container
   centered in the right column. */
.progress-card {
  background: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem; /* rounded-2xl = 16px */
  padding: 48px;
  width: 100%;
  max-width: 28rem; /* max-w-md */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: var(--sideout-navy);
}

/* Red Hat Display label used throughout the card */
.progress-card__eyebrow,
.progress-card__subtitle,
.progress-card__milestone-label,
.progress-card__counter-label {
  font-family: 'Red Hat Display', system-ui, sans-serif;
}

.progress-card__eyebrow {
  color: var(--sideout-pink);
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

/* Amount block */
.progress-card__amount-block {
  margin-top: 20px;
  width: 100%;
}
.progress-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--sideout-emerald);
}
.progress-card__amount-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  stroke: currentColor;
}
.progress-card__amount {
  font-size: 48px;
  line-height: 48px;
  font-weight: 800;
  color: currentColor;
}
.progress-card__subtitle {
  display: block;
  margin-top: 4px;
  color: var(--sideout-muted);
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.05em; /* tracking-wider */
  text-transform: uppercase;
}

/* Progress bar group */
.progress-card__bar-group {
  margin-top: 24px;
  width: 100%;
}
.progress-card__bar-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}
.progress-card__pct {
  color: var(--sideout-navy);
  font-size: 18px;
  line-height: 28px;
  font-weight: 700;
}
.progress-card__to-go {
  color: var(--sideout-muted);
  font-size: 16px;
  line-height: 24px;
}
.progress-card__track {
  height: 20px;
  background: #F3F4F6;
  border-radius: 9999px;
  overflow: hidden;
}
.progress-card__fill {
  height: 100%;
  background: #16a34a; /* green-600, matches the amount figure color */
  border-radius: 9999px;
  transition: width 700ms cubic-bezier(0.4, 0, 0.2, 1);
}
/* Milestones: absolute-positioned dot-over-label markers */
.progress-card__milestones {
  position: relative;
  margin-top: 8px;
  height: 28px;
  width: 100%;
}
.progress-card__milestone {
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.progress-card__milestone-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #E5E7EB; /* gray-200 */
}
.progress-card__milestone--reached .progress-card__milestone-dot {
  background: #22C55E; /* green-500 */
}
.progress-card__milestone-label {
  margin-top: 2px;
  font-size: 10px;
  line-height: 15px;
  font-weight: 400;
  color: rgba(98, 104, 132, 0.5);
  white-space: nowrap;
}
.progress-card__milestone--reached .progress-card__milestone-label {
  color: #16A34A; /* green-600 */
  font-weight: 700;
}

/* Counters — inline icon + value + label */
.progress-card__counters {
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
}
.progress-card__counter {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.progress-card__counter--link {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  border-radius: 6px;
  padding: 4px 8px;
  margin: -4px -8px;
  transition: background-color 0.15s ease, color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}
.progress-card__counter--link:hover {
  background-color: rgba(245, 158, 11, 0.10); /* amber-500 @ 10% */
}
.progress-card__counter--link:hover .progress-card__counter-value {
  color: #B45309; /* amber-700 — subtle darken on hover */
}
.progress-card__counter--link:focus-visible {
  outline: 2px solid #F59E0B;
  outline-offset: 2px;
}
.progress-card__counter-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  stroke: currentColor;
}
.progress-card__counter-icon--amber { color: #F59E0B; } /* amber-500 */
.progress-card__counter-icon--pink  { color: var(--sideout-pink); }
.progress-card__counter-value {
  color: var(--sideout-navy);
  font-size: 18px;
  line-height: 28px;
  font-weight: 800;
}
.progress-card__counter-label {
  color: var(--sideout-muted);
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 400;
}

/* Actions: full-width Donate stacked over a text link. The hairline
   border above replaces what used to be a 2-up row (Donate + Share)
   with a focused vertical stack — Donate is the primary CTA, the link
   below is the "Other ways to support" secondary path that anchors
   into V2SupportOnboarding. */
.progress-card__actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--sideout-border);
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}
.progress-card__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  border-radius: 9999px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  letter-spacing: 0.025em;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.2s, filter 0.2s;
  border: 0;
  white-space: nowrap;
}
.progress-card__btn-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  stroke: currentColor;
  flex-shrink: 0;
}
.progress-card__btn--donate {
  width: 100%;
  background-color: var(--sideout-pink);
  color: #ffffff;
}
.progress-card__btn--donate:hover {
  transform: scale(1.03);
  filter: brightness(1.08);
}
.progress-card__btn--donate:active {
  transform: scale(1);
}

/* Secondary text link — fine-print style. Sentence case + reserved
   typography so it reads as a quiet alternative to the Donate pill,
   not a competing CTA. `white-space: nowrap` keeps it on one line
   inside the 28rem-wide progress card at every breakpoint. */
.progress-card__support-link {
  display: inline-block;
  text-align: center;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--sideout-muted);
  text-decoration: underline;
  text-decoration-color: rgba(98, 104, 132, 0.35);
  text-underline-offset: 3px;
  white-space: nowrap;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
}
.progress-card__support-link:hover {
  color: var(--sideout-pink);
  text-decoration-color: var(--sideout-pink);
}
.progress-card__support-link:focus-visible {
  outline: 2px solid var(--sideout-pink);
  outline-offset: 2px;
}

/* Hero eyebrow (campaign name on subcampaigns / foundation badge).
   Base size lives here now (moved off the inline :style) so it can be
   bumped at the phone breakpoint below. */
.v2-hero__eyebrow {
  font-size: 14px;
}

/* Mobile font bump — client feedback 2026-05-29 ("fonts super small on
   mobile, take them up a couple points"). The hero copy + progress card
   carried desktop type sizes straight down to 390px phones, where the
   small label/link tiers read too tight. Nudge the small + secondary
   tiers up ~2px at the phone breakpoint. The large display figures
   (40px headline, the $48px amount, the 18px % / counter stats) already
   read large and are intentionally left untouched. The hero body is
   bumped 16→18 via its Tailwind class (text-base→text-lg) in the
   template, so it inherits down into the v-html paragraphs. */
@media (max-width: 640px) {
  .v2-hero__eyebrow               { font-size: 16px; } /* 14 → 16 */
  .progress-card__subtitle        { font-size: 18px; } /* 16 → 18 */
  .progress-card__to-go           { font-size: 18px; } /* 16 → 18 */
  .progress-card__milestone-label { font-size: 12px; } /* 10 → 12 */
  .progress-card__counter-label   { font-size: 14px; } /* 12 → 14 */
  .progress-card__support-link    { font-size: 15px; } /* 13 → 15 */
  .progress-card__btn             { font-size: 18px; } /* 16 → 18 */
}

@media (max-width: 480px) {
  .progress-card {
    padding: 32px 24px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-card__btn {
    transition-duration: 60ms;
  }
  .progress-card__btn--donate:hover,
  .progress-card__btn--donate:active {
    transform: none;
  }
  .progress-card__fill {
    transition-duration: 0ms;
  }
}
</style>
